import { type ReactNode } from 'react';

export function PageHeader({ title, subtitle, actions }: { title: string; subtitle?: string; actions?: ReactNode }) {
  return (
    <div className="mb-6 flex flex-wrap items-end justify-between gap-3">
      <div>
        <h1 className="text-xl font-bold text-slate-900">{title}</h1>
        {subtitle ? <p className="mt-1 text-sm text-slate-500">{subtitle}</p> : null}
      </div>
      {actions ? <div className="flex items-center gap-2">{actions}</div> : null}
    </div>
  );
}

export function StatCard({ label, value, hint, tone = 'default' }: { label: string; value: ReactNode; hint?: string; tone?: 'default' | 'good' | 'warn' | 'bad' }) {
  const toneClass =
    tone === 'good' ? 'text-seagreen-600' : tone === 'warn' ? 'text-amber-600' : tone === 'bad' ? 'text-rose-600' : 'text-slate-900';
  return (
    <div className="card px-4 py-3.5">
      <div className="text-xs font-semibold uppercase tracking-wide text-slate-500">{label}</div>
      <div className={`mt-1 text-lg font-bold tabular-nums ${toneClass}`}>{value}</div>
      {hint ? <div className="mt-0.5 text-xs text-slate-400">{hint}</div> : null}
    </div>
  );
}

// One entry per status/enum string the M39 Elections and M40 Ombudsman, ACCS
// & Complaints APIs actually return (see src/lib/types.ts). Unmapped
// statuses still render, just in the neutral slate tone.
const STATUS_TONES: Record<string, string> = {
  // ── M39 Election.state ────────────────────────────────────────────────
  SCHEDULED: 'bg-sky-50 text-sky-700 ring-sky-200',
  ROLL_REVISION: 'bg-indigo-50 text-indigo-700 ring-indigo-200',
  NOMINATIONS_OPEN: 'bg-violet-50 text-violet-700 ring-violet-200',
  NOMINATIONS_CLOSED: 'bg-amber-50 text-amber-700 ring-amber-200',
  POLLING: 'bg-amber-50 text-amber-700 ring-amber-200',
  COUNTING: 'bg-amber-50 text-amber-700 ring-amber-200',
  RESULTS_PUBLISHED: 'bg-emerald-50 text-emerald-700 ring-emerald-200',
  CLOSED: 'bg-slate-100 text-slate-500 ring-slate-200',
  CANCELLED: 'bg-rose-50 text-rose-700 ring-rose-200',

  // ── M39 PartyStatus / PollingStationStatus ────────────────────────────
  ACTIVE: 'bg-emerald-50 text-emerald-700 ring-emerald-200',
  SUSPENDED: 'bg-amber-50 text-amber-700 ring-amber-200',
  DEREGISTERED: 'bg-rose-50 text-rose-700 ring-rose-200',
  PLANNED: 'bg-sky-50 text-sky-700 ring-sky-200',
  // CLOSED already mapped above.

  // ── M39 VoterStatus ─────────────────────────────────────────────────────
  PENDING_CONFIRMATION: 'bg-amber-50 text-amber-700 ring-amber-200',
  // ACTIVE already mapped above.
  INACTIVE: 'bg-slate-100 text-slate-500 ring-slate-200',
  REMOVED_DECEASED: 'bg-slate-200 text-slate-600 ring-slate-300',
  REMOVED_INELIGIBLE: 'bg-rose-50 text-rose-700 ring-rose-200',
  REMOVED_OTHER: 'bg-rose-50 text-rose-700 ring-rose-200',

  // ── M39 RollRevisionState / ClaimObjectionStatus ──────────────────────
  CLAIMS_OPEN: 'bg-violet-50 text-violet-700 ring-violet-200',
  OBJECTIONS_OPEN: 'bg-violet-50 text-violet-700 ring-violet-200',
  REGISTRAR_REVIEW: 'bg-amber-50 text-amber-700 ring-amber-200',
  CERTIFIED: 'bg-emerald-50 text-emerald-700 ring-emerald-200',
  SUBMITTED: 'bg-sky-50 text-sky-700 ring-sky-200',
  UNDER_REVIEW: 'bg-amber-50 text-amber-700 ring-amber-200',
  UPHELD: 'bg-emerald-50 text-emerald-700 ring-emerald-200',
  REJECTED: 'bg-rose-50 text-rose-700 ring-rose-200',

  // ── M39 NominationStatus / DepositStatus ──────────────────────────────
  ELIGIBILITY_CHECK: 'bg-amber-50 text-amber-700 ring-amber-200',
  DEPOSIT_PENDING: 'bg-amber-50 text-amber-700 ring-amber-200',
  ACCEPTED: 'bg-emerald-50 text-emerald-700 ring-emerald-200',
  // REJECTED already mapped above.
  WITHDRAWN: 'bg-slate-100 text-slate-500 ring-slate-200',
  PENDING: 'bg-sky-50 text-sky-700 ring-sky-200',
  PAID: 'bg-emerald-50 text-emerald-700 ring-emerald-200',
  REFUND_DUE: 'bg-emerald-50 text-emerald-700 ring-emerald-200',
  FORFEITED: 'bg-rose-50 text-rose-700 ring-rose-200',

  // ── M39 EligibilityCheckStatus ─────────────────────────────────────────
  PASS: 'bg-emerald-50 text-emerald-700 ring-emerald-200',
  FAIL: 'bg-rose-50 text-rose-700 ring-rose-200',
  NEEDS_REVIEW: 'bg-amber-50 text-amber-700 ring-amber-200',
  UNAVAILABLE: 'bg-slate-100 text-slate-500 ring-slate-200',

  // ── M39 TallyStatus ─────────────────────────────────────────────────────
  DRAFT: 'bg-slate-100 text-slate-600 ring-slate-200',
  // SUBMITTED already mapped above.
  VERIFIED: 'bg-emerald-50 text-emerald-700 ring-emerald-200',
  DISPUTED: 'bg-rose-50 text-rose-700 ring-rose-200',

  // ── M39 ResultsPublication.status / .outcome ──────────────────────────
  // CERTIFIED already mapped above.
  PUBLISHED: 'bg-emerald-50 text-emerald-700 ring-emerald-200',
  WINNER_DECLARED: 'bg-emerald-50 text-emerald-700 ring-emerald-200',
  TIE: 'bg-amber-50 text-amber-700 ring-amber-200',
  RUNOFF_REQUIRED: 'bg-amber-50 text-amber-700 ring-amber-200',

  // ── M40 Complaint.state ─────────────────────────────────────────────────
  RECEIVED: 'bg-sky-50 text-sky-700 ring-sky-200',
  REFERRED_TO_MDA: 'bg-indigo-50 text-indigo-700 ring-indigo-200',
  UNDER_INVESTIGATION: 'bg-violet-50 text-violet-700 ring-violet-200',
  REFERRED_TO_ACCS: 'bg-rose-50 text-rose-700 ring-rose-200',
  AWAITING_COMPLAINANT: 'bg-amber-50 text-amber-700 ring-amber-200',
  RESOLVED: 'bg-emerald-50 text-emerald-700 ring-emerald-200',
  SIGNPOSTED: 'bg-slate-100 text-slate-500 ring-slate-200',
  // WITHDRAWN already mapped above.

  // ── M40 ComplaintOutcome ────────────────────────────────────────────────
  // UPHELD collides visually with roll UPHELD above (both emerald) — fine.
  PARTIALLY_UPHELD: 'bg-amber-50 text-amber-700 ring-amber-200',
  NOT_UPHELD: 'bg-rose-50 text-rose-700 ring-rose-200',
  RESOLVED_INFORMALLY: 'bg-emerald-50 text-emerald-700 ring-emerald-200',
  DISCONTINUED: 'bg-slate-100 text-slate-500 ring-slate-200',

  // ── M40 WhistleblowerStatus ─────────────────────────────────────────────
  CONVERTED_TO_CASE: 'bg-emerald-50 text-emerald-700 ring-emerald-200',
  DECLINED_INSUFFICIENT: 'bg-rose-50 text-rose-700 ring-rose-200',
  DECLINED_OUT_OF_SCOPE: 'bg-rose-50 text-rose-700 ring-rose-200',
  CLOSED_NO_ACTION: 'bg-slate-100 text-slate-500 ring-slate-200',

  // ── M40 IntegrityCaseStatus ─────────────────────────────────────────────
  OPENED: 'bg-sky-50 text-sky-700 ring-sky-200',
  PRELIMINARY_REVIEW: 'bg-indigo-50 text-indigo-700 ring-indigo-200',
  FULL_INVESTIGATION: 'bg-violet-50 text-violet-700 ring-violet-200',
  REFERRED_TO_PROSECUTION: 'bg-emerald-50 text-emerald-700 ring-emerald-200',
  CLOSED_INSUFFICIENT_EVIDENCE: 'bg-slate-100 text-slate-500 ring-slate-200',
  CLOSED_UNSUBSTANTIATED: 'bg-slate-100 text-slate-500 ring-slate-200',
  CLOSED_ADMIN_ACTION: 'bg-slate-100 text-slate-500 ring-slate-200',
  CLOSED_REFERRED_OUT: 'bg-slate-100 text-slate-500 ring-slate-200',

  // ── M40 ProsecutionReferralStatus ──────────────────────────────────────
  NOT_REFERRED: 'bg-slate-100 text-slate-500 ring-slate-200',
  PENDING_TRANSMISSION: 'bg-amber-50 text-amber-700 ring-amber-200',
  TRANSMITTED: 'bg-emerald-50 text-emerald-700 ring-emerald-200',

  // ── M40 SystemicIssueStatus ─────────────────────────────────────────────
  // DRAFT/PUBLISHED already mapped above.

  // ── M40 DeclarationStatus ───────────────────────────────────────────────
  FLAGGED: 'bg-amber-50 text-amber-700 ring-amber-200',
  CLEARED: 'bg-emerald-50 text-emerald-700 ring-emerald-200',
  NON_COMPLIANT: 'bg-rose-50 text-rose-700 ring-rose-200',

  // ── M40 InvestigatorRole (rendered via StatusBadge on the team list) ───
  LEAD: 'bg-indigo-50 text-indigo-700 ring-indigo-200',
  MEMBER: 'bg-slate-100 text-slate-600 ring-slate-200',
};

export function StatusBadge({ status }: { status: string }) {
  const tone = STATUS_TONES[status] ?? 'bg-slate-100 text-slate-600 ring-slate-200';
  return (
    <span className={`inline-flex items-center rounded-full px-2 py-0.5 text-[11px] font-semibold ring-1 ${tone}`}>
      {status.replaceAll('_', ' ')}
    </span>
  );
}

export function Spinner({ label = 'Loading…' }: { label?: string }) {
  return (
    <div className="flex items-center gap-2 px-4 py-8 text-sm text-slate-500">
      <span className="h-4 w-4 animate-spin rounded-full border-2 border-slate-300 border-t-brand-600" />
      {label}
    </div>
  );
}

export function ErrorNote({ message }: { message: string }) {
  return (
    <div className="rounded-lg border border-rose-200 bg-rose-50 px-3.5 py-2.5 text-sm text-rose-700">{message}</div>
  );
}

export function EmptyState({ title, hint }: { title: string; hint?: string }) {
  return (
    <div className="px-4 py-10 text-center">
      <div className="text-sm font-semibold text-slate-600">{title}</div>
      {hint ? <div className="mt-1 text-xs text-slate-400">{hint}</div> : null}
    </div>
  );
}

export function Modal({ title, onClose, children, wide }: { title: string; onClose: () => void; children: ReactNode; wide?: boolean }) {
  return (
    <div className="fixed inset-0 z-50 flex items-start justify-center overflow-y-auto bg-ink-950/50 p-4 pt-[8vh]" onClick={onClose}>
      <div
        className={`card w-full ${wide ? 'max-w-3xl' : 'max-w-lg'} p-5`}
        onClick={(e) => e.stopPropagation()}
        role="dialog"
        aria-modal="true"
      >
        <div className="mb-4 flex items-center justify-between">
          <h2 className="text-base font-bold text-slate-900">{title}</h2>
          <button className="rounded-md px-2 py-1 text-slate-400 hover:bg-slate-100 hover:text-slate-600" onClick={onClose} aria-label="Close">
            ✕
          </button>
        </div>
        {children}
      </div>
    </div>
  );
}

export function Field({ label, children, className = '' }: { label: string; children: ReactNode; className?: string }) {
  return (
    <label className={`block ${className}`}>
      <span className="field-label">{label}</span>
      {children}
    </label>
  );
}

/** Small pill-tab bar — same visual shape every page of this portal uses to switch panels. */
export function Tabs<K extends string>({
  tabs,
  value,
  onChange,
}: {
  tabs: ReadonlyArray<{ key: K; label: string }>;
  value: K;
  onChange: (key: K) => void;
}) {
  return (
    <div className="mb-4 flex gap-1 rounded-xl bg-slate-200/60 p-1">
      {tabs.map((t) => (
        <button
          key={t.key}
          onClick={() => onChange(t.key)}
          className={`flex-1 rounded-lg px-3 py-1.5 text-sm font-semibold transition ${
            value === t.key ? 'bg-white text-slate-900 shadow-sm' : 'text-slate-500 hover:text-slate-700'
          }`}
        >
          {t.label}
        </button>
      ))}
    </div>
  );
}

/** Inline warning banner for "your persona lacks role X" — same message pattern used throughout the fleet's portals. */
export function RoleNotice({ roles }: { roles: string[] }) {
  return (
    <div className="mb-4">
      <ErrorNote message={`Requires ${roles.join(' or ')} — the actions below will be rejected by the API (403). You can still view this page.`} />
    </div>
  );
}
